import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import IdeaCard from "@/components/ideas/IdeaCard";
import NewIdeaDialog from "@/components/ideas/NewIdeaDialog";
import SetupChecklist from "@/components/onboarding/SetupChecklist";
import { Button } from "@/components/ui/button";
import { Plus, Lightbulb, Sparkles } from "lucide-react";

const CATEGORIES = ["All", "Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];

const IdeasBoard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [grantReadyOnly, setGrantReadyOnly] = useState(false);
  const [newIdeaOpen, setNewIdeaOpen] = useState(false);

  // Auto-open NewIdeaDialog when arriving from /welcome with ?new=1
  useEffect(() => {
    if (!user) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("new") === "1") {
      setNewIdeaOpen(true);
      url.searchParams.delete("new");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [user]);

  const { data: ideas = [], isLoading } = useQuery({
    queryKey: ["ideas", selectedCategory],
    queryFn: async () => {
      let query = supabase
        .from("ideas")
        .select("*")
        .eq("status", "published")
        .order("vote_count", { ascending: false });

      if (selectedCategory !== "All") {
        query = query.eq("category", selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch profiles for all unique user_ids
      const userIds = [...new Set(data.map((i) => i.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.user_id, p])
      );

      return data.map((idea) => ({
        ...idea,
        profiles: profileMap.get(idea.user_id) || null,
      }));
    },
  });

  const { data: userVotes = [] } = useQuery({
    queryKey: ["user-votes", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_votes")
        .select("idea_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data.map((v) => v.idea_id);
    },
    enabled: !!user,
  });

  // Authenticated-only: which ideas have a grant-ready brief
  const { data: grantReadyIds = [] } = useQuery({
    queryKey: ["grant-ready-set"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_grant_details")
        .select("idea_id");
      if (error) throw error;
      return data.map((r) => r.idea_id);
    },
    enabled: !!user,
  });

  const grantReadySet = new Set<string>(grantReadyIds);
  const visibleIdeas = grantReadyOnly
    ? ideas.filter((i: any) => grantReadySet.has(i.id))
    : ideas;

  const voteMutation = useMutation({
    mutationFn: async ({ ideaId, hasVoted }: { ideaId: string; hasVoted: boolean }) => {
      if (!user) {
        navigate("/auth");
        return;
      }
      if (hasVoted) {
        const { error } = await supabase
          .from("idea_votes")
          .delete()
          .eq("idea_id", ideaId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("idea_votes")
          .insert({ idea_id: ideaId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ideas"] });
      queryClient.invalidateQueries({ queryKey: ["user-votes"] });
    },
  });

  const handleNewIdea = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setNewIdeaOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6">
          <SetupChecklist variant="inline" className="mb-6" />
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3">
                <Lightbulb className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold">Discover</h1>
              </div>
              <p className="text-muted-foreground mt-1">
                Actionable ideas from the shareholder community. Upvote what matters.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleNewIdea} className="gap-2">
                <Plus className="h-4 w-4" />
                Share an Idea
              </Button>
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                {cat}
              </button>
            ))}
            {user && (
              <button
                onClick={() => setGrantReadyOnly((v) => !v)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  grantReadyOnly
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Grant-Ready
              </button>
            )}
          </div>

          {/* Masonry grid */}
          {isLoading ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl h-64 mb-4 animate-pulse break-inside-avoid" />
              ))}
            </div>
          ) : visibleIdeas.length === 0 ? (
            <div className="text-center py-20">
              <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {grantReadyOnly ? "No grant-ready ideas yet" : "No ideas yet"}
              </h2>
              <p className="text-muted-foreground mb-6">
                {grantReadyOnly
                  ? "Be the first to add a grant-ready brief to an idea."
                  : "Be the first to share an actionable idea!"}
              </p>
              <Button onClick={handleNewIdea} className="gap-2">
                <Plus className="h-4 w-4" /> Share an Idea
              </Button>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {visibleIdeas.map((idea: any) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  hasVoted={userVotes.includes(idea.id)}
                  onVote={(hasVoted) => voteMutation.mutate({ ideaId: idea.id, hasVoted })}
                  currentUserId={user?.id ?? null}
                  isGrantReady={grantReadySet.has(idea.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      {user && <NewIdeaDialog open={newIdeaOpen} onOpenChange={setNewIdeaOpen} />}
    </div>
  );
};

export default IdeasBoard;
